import { useEffect, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useAppContext } from '@/hooks/useAppContext';
import { generateCompleteSitemap } from '@/lib/sitemap';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Sitemap component that generates XML sitemap for SEO
 * Accessible at /sitemap.xml
 */
export default function Sitemap() {
  const { nostr } = useNostr();
  const { config } = useAppContext();
  const [sitemapXml, setSitemapXml] = useState<string>('');

  useEffect(() => {
    let mounted = true;

    const fetchSitemapData = async () => {
      try {
        const gitRelays = config.ngitServers.map((server) => `wss://${server}/`);

        // Fetch all published projects (kind 30617)
        const projects = await nostr
          .group(gitRelays)
          .query(
            [
              {
                kinds: [30617],
                limit: 1000, // Fetch up to 1000 projects
              },
            ],
            { signal: AbortSignal.timeout(10000) }
          );

        if (!mounted) return;

        // Extract unique author pubkeys from projects
        const uniquePubkeys = Array.from(
          new Set(projects.map((project: NostrEvent) => project.pubkey))
        );

        // Generate sitemap XML
        const xml = generateCompleteSitemap(projects, uniquePubkeys);
        setSitemapXml(xml);
      } catch (error) {
        console.error('Failed to generate sitemap:', error);
        // Generate minimal sitemap with static pages only
        if (mounted) {
          setSitemapXml(generateCompleteSitemap([], []));
        }
      }
    };

    fetchSitemapData();

    return () => {
      mounted = false;
    };
  }, [nostr, config.ngitServers]);

  // Render as plain text XML
  return (
    <pre style={{ 
      fontFamily: 'monospace', 
      fontSize: '12px', 
      whiteSpace: 'pre-wrap',
      padding: '20px',
      maxWidth: '100%',
      overflowX: 'auto',
    }}>
      {sitemapXml || 'Generating sitemap...'}
    </pre>
  );
}
